/**
 * The item page — /prioritization/items/:itemId.
 *
 * Renders ItemDetailThirdPassView, the same component the Lab renders, with a
 * model built from the real item (useItemDetailModel). This file is wiring
 * only: it loads the item, keeps the destination in the URL, and owns the
 * dialogs the view opens (Integrations, Retire, Take back, Who can see it,
 * evidence, AI assists, Decision Trail). The dialogs are the app's existing
 * ones, reused — none is redrawn here.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Modal } from '@/components/ds';
import { recordItemFieldChange, useItemsStore } from '@/stores/prioritization/itemsStore';
import { useItemClassificationStore } from '@/stores/prioritization/itemClassificationStore';
import { useTemplateAgents } from '@/hooks/useTemplateAgents';
import { useAgentReviewerAllowed } from '@/hooks/useAgentReviewerAllowed';
import { useWatchesStore } from '@/stores/prioritization/watchesStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useAiCapability } from '@/lib/ai/useAiCapability';
import { useItemOutcomes } from '@/hooks/prioritization/useItemOutcomes';
import { useDecisionTrailParam } from '@/hooks/prioritization/useDecisionTrailParam';
import { useToast } from '@/components/ui/Toast';
import { isUuid } from '@/lib/isUuid';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { restoreRetiredItem } from '@/lib/reversals/reversalsService';
import { getWorkspaceModules, isModuleEnabled, isModuleFeatureAccessible } from '@/lib/workspaceModulesService';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import { checkPolicyForItem } from '@/lib/authorization/storeEnforcement';
import { DecisionTrailPanel } from '@/components/prioritization/DecisionTrailPanel';
import { ItemDecisionsSection } from '@/components/prioritization/item-view/ItemDecisionsSection';
import { ItemIntegrationsModal } from '@/components/prioritization/item-view/ItemIntegrationsModal';
import { ItemVisibilitySection } from '@/components/prioritization/item-view/ItemVisibilitySection';
import { ItemDeliveryTab } from '@/components/prioritization/item-view/ItemDeliveryTab';
import { ClassificationStatusBanner } from '@/components/prioritization/item-view/ClassificationStatusBanner';
import { InheritedRelationshipsBanner } from '@/components/prioritization/item-view/InheritedRelationshipsBanner';
import { RelationshipGatesBanner } from '@/components/prioritization/RelationshipGatesBanner';
import { ItemRankingsSection } from '@/components/prioritization/item-view/ItemRankingsSection';
import { ItemPatternsSection } from './ItemPatternsSection';
import { AskButton } from '@/components/ask/AskSheet';
import { AssignmentsSection } from '@/components/prioritization/commitments/AssignmentsSection';
import { ItemImpactTab } from '@/components/prioritization/item-view/ItemImpactTab';
import { LineageGraph } from '@/components/prioritization/item-view/LineageGraph';
import { ItemCreationProvenanceControl } from '@/components/prioritization/item-view/ItemCreationProvenanceControl';
import { ItemDatadogSignalsPanel } from '@/components/prioritization/item-view/ItemDatadogSignalsPanel';
import { ItemSnowflakeQueryPanel } from '@/components/prioritization/item-view/ItemSnowflakeQueryPanel';
import { ItemVersionsPanel } from '@/components/prioritization/item-view/ItemVersionsPanel';
import { StructureVersionFinePrint } from '@/components/hierarchy/StructureVersionFinePrint';
import { useAttributeGovernanceStore } from '@/stores/prioritization/attributeGovernanceStore';
import { ItemCustomFields } from './ItemCustomFields';
import { PlanActionBand, type ActionPlan } from './PlanActionBand';
import { cycleInContext, itemAsHeld, listsInCycle, plansAsFacts, plansThatLock, itemInContextHref } from '@/lib/itemCycleStanding';
import { cycleStandingChip } from '@/lib/itemRankings';
import { ItemCycleBand } from './ItemCycleBand';
import { useItemCycleStanding } from './useItemCycleStanding';
import { fetchItemPlanDrift, type ItemPlanDrift } from '@/lib/itemPlanDriftService';
import { ItemLinkedSystemsSection } from '@/components/prioritization/item-view/ItemLinkedSystemsSection';
import { ClaritySection } from '@/components/methodology/MethodologyConnectionChip';
import { getConnectionsForTarget, deleteConnection, type MethodologyConnection } from '@/lib/methodologyConnectionsService';
import { resolveConnectorStatuses } from '@/lib/connectorConfigsService';
import { useItemRelationshipGates } from '@/hooks/prioritization/useItemRelationshipGates';
import { useItemImpactStore } from '@/stores/prioritization/itemImpactStore';
import { useIsAdmin } from '@/hooks/prioritization/useScopedPermissions';
import { assembleItem, type AssembledItem } from '@/lib/assembleItemService';
import type { LineageEventType } from '@/lib/lineageService';
import { btnSecondary, LABEL_MONO } from '@/components/mono/primitives';

import { ItemHistoryLineageView } from '@/components/prioritization/item-view/activity/ItemHistoryLineageView';
import { EntityCommentsTab } from '@/components/prioritization/activity/EntityCommentsTab';
import { EvidenceForm } from '@/components/prioritization/evidence/EvidenceForm';
import { DeliverItemModal } from '@/components/prioritization/DeliverItemModal';
import { RetireItemsDialog } from '@/components/reversals/RetireItemsDialog';
import { ProvenanceActionModal } from '@/components/prioritization/item-view/ProvenanceActionModal';
import { CreateCanonicalClusterModal } from '@/components/prioritization/item-view/CreateCanonicalClusterModal';
import { CreatePackageModal } from '@/components/prioritization/item-view/CreatePackageModal';
import { CreateExclusiveModal } from '@/components/prioritization/item-view/CreateExclusiveModal';
import { AddContainsModal } from '@/components/prioritization/item-view/AddContainsModal';
import { AddSequenceLinkModal } from '@/components/prioritization/item-view/AddSequenceLinkModal';
import { AddCompositionModal } from '@/components/prioritization/item-view/AddCompositionModal';
import { AddElaborationModal } from '@/components/prioritization/item-view/AddElaborationModal';
import { AddCarriesModal } from '@/components/prioritization/item-view/AddCarriesModal';
import { AddToCollectionModal } from '@/components/prioritization/item-view/AddToCollectionModal';
import { useItemRelationshipsStore } from '@/stores/prioritization/itemRelationshipsStore';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { NexusModal } from '@/components/agents/NexusModal';
import { IdeaCraftingModal } from '@/components/agents/IdeaCraftingModal';
import type { Evidence } from '@/types/prioritization/evidence';
import type { DeliveryType } from '@/types/delivery';
import type { CustomFieldPlacementTab, Item } from '@/types/prioritization/lifecycle';
import type { ItemClassificationProposal } from '@/types/prioritization/itemClassification';
import { linkItemToObjective } from '@/apps/align/alignLinking';
import { clearMappingGoal, deleteMapping } from '@/lib/strategyAlignment';
import { ItemDetailThirdPassView, OwnedSectionHeading, type MenuEntry } from './ItemDetailThirdPassView';
import type { LineageActType, RelationshipKind } from './useItemDetailModel';
import type { Destination } from './itemDetailTypes';
import {
  buildItemDetailModel,
  useItemDetailActorNames,
  useItemDetailData,
  useItemDetailStores,
} from './useItemDetailModel';

const DESTINATIONS: Destination[] = ['overview', 'connections', 'assessment', 'prioritization', 'delivery', 'history'];

/** Old tab links (?tab=values, notifications, Slack) land in their destination. */
const LEGACY_TAB_DESTINATION: Record<string, Destination> = {
  classification: 'overview',
  values: 'assessment',
  evidence: 'assessment',
  relationships: 'connections',
  rankings: 'prioritization',
  lineage: 'history',
  activity: 'history',
  discussion: 'overview',
  'pre-planning': 'delivery',
  delivery: 'delivery',
};

function resolveDestination(raw: string | null, pointsAtComment: boolean): Destination {
  if (pointsAtComment) return 'overview';
  if (raw && raw in LEGACY_TAB_DESTINATION) return LEGACY_TAB_DESTINATION[raw];
  return DESTINATIONS.includes(raw as Destination) ? (raw as Destination) : 'overview';
}

export function ItemDetailRealPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const item = useItemsStore((s) => (itemId ? s.items.find((i) => i.id === itemId) : undefined));
  const [loading, setLoading] = useState(true);
  // A whole-list load that began before this item was created takes it back out
  // of the store, so this reacts to the item leaving as well as to the route.
  // Reading the store once showed "Item not found." right after "Create Item".
  const inStore = Boolean(item);

  useEffect(() => {
    if (!itemId || inStore || !isUuid(itemId)) {
      // Non-UUID segments ("/items/intake") match this route but can never be an item.
      setLoading(false);
      return;
    }
    setLoading(true);
    loadItemById(itemId).finally(() => setLoading(false));
  }, [itemId, inStore, loadItemById]);

  if (!itemId) {
    return <Missing text="No item selected." />;
  }
  if (loading && !item) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <span className="text-[var(--text-ter)]">Loading item…</span>
      </div>
    );
  }
  if (!item) return <Missing text="Item not found." />;
  return <ItemDetailLoaded item={item} />;
}

function Missing({ text }: { text: string }) {
  return (
    <div className="p-6">
      <p className="text-[var(--text-ter)]">{text}</p>
      <Link to="/prioritization/items" className="mt-2 inline-block text-[var(--text-ter)] hover:underline">
        Back to Items
      </Link>
    </div>
  );
}

function ItemDetailLoaded({ item }: { item: Item }) {
  const itemId = item.id;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const updateItem = useItemsStore((s) => s.updateItem);
  const loadItemById = useItemsStore((s) => s.loadItemById);
  const transferOwnership = useItemsStore((s) => s.transferOwnership);
  const userId = useUserStore((s) => s.userId);
  // Capabilities arrive after first render; subscribing re-draws the page when they do.
  const capabilitySignature = useUserStore((s) => `${s.permissionsLoaded}:${s.resolvedCapabilityIds.length}:${s.assignedRoleNames.join(',')}`);

  // ── Destination, kept in the URL ──
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const pointsAtComment = searchParams.has('comment') || searchParams.has('anchor');
  const tab: Destination = resolveDestination(rawTab, pointsAtComment);
  const setTab = useCallback(
    (t: Destination) => {
      setSearchParams(
        (prev) => {
          prev.set('tab', t);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  useEffect(() => {
    if (rawTab !== tab) setTab(tab);
  }, [rawTab, tab, setTab]);

  // ── Data ──
  const data = useItemDetailData(itemId, item);
  const st = useItemDetailStores(item);
  const names = useItemDetailActorNames(item, data, st);

  // Load the criteria of every track the Prioritization tab draws: tracks whose pool
  // holds the item, and tracks of the sessions that ranked it.
  const fetchTrackCriteria = useAttributeGovernanceStore((s) => s.fetchTrackConfigs);
  const shownTrackIds = useMemo(() => {
    const ids = new Set<string>();
    for (const p of st.itemPools) if (p.trackId && p.priorityIds?.includes(itemId)) ids.add(p.trackId);
    for (const rv of data.rankedVersions) {
      const trackId = st.sessions.find((x) => x.id === rv.sessionId)?.trackId;
      if (trackId) ids.add(trackId);
    }
    return [...ids].sort().join(',');
  }, [st.itemPools, st.sessions, data.rankedVersions, itemId]);
  useEffect(() => {
    for (const id of shownTrackIds ? shownTrackIds.split(',') : []) void fetchTrackCriteria(id);
  }, [shownTrackIds, fetchTrackCriteria]);

  // ── Action: the item is in a Plan of Record in force (owner, 2026-10-15) ──
  // The plan's rank of record sits on top and Planning & delivery comes right after
  // Overview; the band's "How it was ranked" opens the Prioritization tab; ?plan= picks
  // among several plans. (The Action / How it was ranked switch is gone, owner 2026-10-16.)
  const actionPlans: ActionPlan[] = useMemo(
    () =>
      data.rankings
        .filter((r) => r.standing === 'official' && r.planCycleId)
        .map((r) => ({
          planCycleId: r.planCycleId!,
          name: r.sourceName,
          href: r.sourceHref ?? `/planning/plans/${r.planCycleId}`,
          cycleId: r.cycleId ?? null,
          rank: r.rank,
          aboveTheLine: r.aboveTheLine,
          authorizedAt: r.at,
          authorizedBy: r.by ? names[r.by] ?? null : null,
        })),
    [data.rankings, names],
  );
  // Which cycle this page was opened from (roadmap S-37). Only that cycle's lock applies:
  // in a cycle still ranking, what another cycle fixed is a fact, shown in the band above.
  const cycleParam = searchParams.get('cycle');
  const planParam = searchParams.get('plan');
  const heldKey = `${item.title}|${item.description ?? ''}|${item.itemCategory ?? ''}|${item.altitudeLevel ?? ''}|${item.effortCapacityUnits ?? ''}|${item.work_type ?? ''}|${item.launch_type ?? ''}|${item.investment_type ?? ''}|${item.frames?.length ?? 0}|${data.rankings.length}`;
  const standing = useItemCycleStanding(itemId, cycleParam, planParam, heldKey);
  const cycleShown = cycleInContext(standing);
  const lockingPlans = useMemo(() => plansThatLock(actionPlans, standing), [actionPlans, standing]);
  const activePlan = lockingPlans.find((p) => p.planCycleId === planParam) ?? lockingPlans[0] ?? null;
  const inAction = activePlan !== null;
  // In Action the page shows the item as its plan holds it; a newer version stays out until the plan is amended.
  const planHold = activePlan ? standing?.plans.find((p) => p.planCycleId === activePlan.planCycleId) ?? null : null;
  const shownItem = planHold ? itemAsHeld(item, planHold.held) : item;
  const cycleOfTrack = useCallback((trackId: string) => st.tracks.find((t) => t.id === trackId)?.cycleId ?? null, [st.tracks]);
  const setParam = useCallback(
    (key: string, value: string | null) =>
      setSearchParams(
        (prev) => {
          if (value === null) prev.delete(key);
          else prev.set(key, value);
          return prev;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  // What has drifted since the plan was authorized (migration 936) — Needs attention, in Action.
  const [drift, setDrift] = useState<ItemPlanDrift | null>(null);
  const [driftReload, setDriftReload] = useState(0);
  const driftPlanId = inAction ? activePlan?.planCycleId ?? null : null;
  useEffect(() => {
    setDrift(null);
    if (!driftPlanId) return;
    let live = true;
    fetchItemPlanDrift(itemId, driftPlanId)
      .then((d) => live && setDrift(d))
      .catch(() => live && setDrift({ authorizedAt: null, recorded: false, mayDecide: false, lines: [] }));
    return () => {
      live = false;
    };
  }, [itemId, driftPlanId, driftReload]);

  // The page opens on Overview unless the link names a tab — also when the item is
  // in a plan of record in force (owner, 2026-10-16: links from Home opened the
  // second tab, Planning & delivery). Planning & delivery stays right after Overview.
  useEffect(() => {
    if (data.commitmentsEnabled && !st.commitmentsSynced) void st.fetchCommitments();
  }, [data.commitmentsEnabled, st.commitmentsSynced, st.fetchCommitments]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [impactEnabled, setImpactEnabled] = useState(false);
  const fetchImpact = useItemImpactStore((s) => s.fetchForItem);
  useEffect(() => {
    if (impactEnabled) void fetchImpact(itemId);
  }, [impactEnabled, itemId, fetchImpact]);

  // The parts the earlier item page carried, with the same gates.
  const [clarity, setClarity] = useState<MethodologyConnection[]>([]);
  useEffect(() => {
    let live = true;
    void getConnectionsForTarget('item', itemId).then(({ data: rows }) => live && setClarity(rows));
    return () => {
      live = false;
    };
  }, [itemId]);
  const [connectors, setConnectors] = useState({ datadog: false, snowflake: false });
  useEffect(() => {
    const ws = getCurrentWorkspaceId();
    if (!ws) return;
    let live = true;
    resolveConnectorStatuses(ws, userId ?? null, ['datadog', 'snowflake'])
      .then((statuses) => live && setConnectors({ datadog: statuses.datadog === 'active', snowflake: statuses.snowflake === 'active' }))
      .catch(() => live && setConnectors({ datadog: false, snowflake: false }));
    return () => {
      live = false;
    };
  }, [userId]);
  const gates = useItemRelationshipGates({ itemId, activeFrameId: item.canonicalFrameId ?? null });
  const gateIssues = gates.outcomes.filter((o) => o.level === 'block' || o.level === 'require_approval');
  const classificationState = useItemClassificationStore((s) => s.byItem[itemId]);
  const atcUnused = classificationState?.activeProposal?.status !== 'active' && !(classificationState?.history ?? []).some((p) => p.status === 'accepted');
  const isAdmin = useIsAdmin();
  const [debug, setDebug] = useState<{ data: AssembledItem | null; error: string | null; loading: boolean } | null>(null);
  const openDebug = async () => {
    setDebug({ data: null, error: null, loading: true });
    const { data: assembled, error } = await assembleItem(itemId);
    setDebug({ data: assembled, error: error ?? null, loading: false });
  };
  const lineageForGraph = data.lineage.map((row) => ({
    id: row.id,
    eventType: row.event_type as LineageEventType,
    sourceItemIds: row.source_item_ids ?? [],
    targetItemIds: row.target_item_ids ?? [],
    performedBy: row.performed_by ?? '',
    createdAt: row.created_at,
    rationale: row.rationale ?? undefined,
    metadata: row.metadata ?? undefined,
  }));
  useEffect(() => {
    if (!st.workspaceId) return;
    let live = true;
    getWorkspaceModules(st.workspaceId).then(({ data: modules }) => {
      if (!live) return;
      setDeliveryEnabled(isModuleFeatureAccessible(modules, 'delivery_tracking', FEATURE_FLAGS.DELIVERY_TRACKING));
      setImpactEnabled(isModuleEnabled(modules, 'cost_of_delay'));
    });
    return () => {
      live = false;
    };
  }, [st.workspaceId]);

  // ── Dialogs and panels ──
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [visibilityOpen, setVisibilityOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [lineageAct, setLineageAct] = useState<LineageActType | null>(null);
  const [takeBackOpen, setTakeBackOpen] = useState(false);
  const [evidenceForm, setEvidenceForm] = useState<{ evidence: Evidence | null } | null>(null);
  const [relationshipKind, setRelationshipKind] = useState<RelationshipKind | null>(null);
  const refreshRelationships = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const [nexusOpen, setNexusOpen] = useState(false);
  const [descriptorOpen, setDescriptorOpen] = useState(false);
  const [deliver, setDeliver] = useState<{ mode: 'initial' | 'revision'; previous: DeliveryType | null } | null>(null);
  const [deliveryRefresh, setDeliveryRefresh] = useState(0);
  const { trailItemId, openTrail, closeTrail } = useDecisionTrailParam();

  const outcomeIds = useMemo(() => [itemId], [itemId]);
  const { outcomes } = useItemOutcomes(outcomeIds, item.exit_status);
  // Same gate the old item page drew its lineage control behind (items.edit).
  void capabilitySignature;
  const mayEditItem = checkPolicyForItem('items.edit', itemId, item as unknown as Record<string, unknown>).allowed;
  const mayTakeBack = item.exit_status === 'archived' && outcomes.get(itemId)?.youMayRestore === true;

  // ── AI assists (what the agents bar offered) ──
  // The classifier the item page offers: the Air Traffic Controller, or a custom classify agent, when on and set to run on one item.
  const classifier = useTemplateAgents('classify').find((a) => a.settings?.enabled && (a.settings.triggers as { manualSingle?: boolean } | undefined)?.manualSingle !== false) ?? null;
  // Nexus on the template (migration 941): the item page offers its advisor when the agent is on.
  const nexusSettings = useTemplateAgents('propose_relationship').find((a) => a.def.agentType === 'nexus')?.settings ?? null;
  const nexusConfig = nexusSettings?.enabled && (nexusSettings.triggers as { manualSingle?: boolean } | undefined)?.manualSingle !== false ? nexusSettings : null;
  const descriptorEnabled = useAiCapability('idea_crafting');
  const runAtcAndPropose = useItemClassificationStore((s) => s.runAtcAndPropose);
  const acceptProposal = useItemClassificationStore((s) => s.accept);
  const rejectProposal = useItemClassificationStore((s) => s.reject);
  const [proposalBusy, setProposalBusy] = useState(false);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const [proposalDecided, setProposalDecided] = useState<{ proposal: ItemClassificationProposal; text: string } | null>(null);
  const activeProposal = st.classification?.activeProposal ?? null;
  // The item row may come without its workspace id (it is "" then); the page's workspace is the same one.
  const itemWorkspaceId = item.workspace_id || getCurrentWorkspaceId() || '';
  const mayDecideProposal = useAgentReviewerAllowed(itemWorkspaceId, activeProposal?.proposedBy === 'agent' ? activeProposal.proposedByAgent ?? null : null);

  const runAtc = useCallback(async () => {
    if (!classifier) return;
    const result = await runAtcAndPropose({
      itemId,
      workspaceId: itemWorkspaceId,
      title: item.title,
      description: item.description ?? '',
      agent: { agentType: classifier.def.agentType, name: classifier.def.name },
      runByUserId: userId ?? null,
    });
    if (!result.success) showToast('error', result.error ?? 'The classifier could not run.');
  }, [classifier, runAtcAndPropose, itemId, itemWorkspaceId, item.title, item.description, userId, showToast]);

  const askAi: MenuEntry[] = [
    ...(descriptorEnabled && (item.title?.trim() || item.description?.trim())
      ? [{ label: 'Write the title or description from the other', onClick: () => setDescriptorOpen(true) }]
      : []),
    ...(classifier && !activeProposal ? [{ label: 'Suggest a category and altitude', onClick: () => void runAtc() }] : []),
    ...(nexusConfig?.enabled ? [{ label: 'Find how it relates to other items', onClick: () => setNexusOpen(true) }] : []),
  ];

  const decideProposal = async (accept: boolean) => {
    if (!activeProposal) return;
    if (accept && !userId) {
      setProposalError('Sign in again to accept the suggestion.');
      return;
    }
    setProposalBusy(true);
    setProposalError(null);
    const snapshot = activeProposal;
    const result = accept
      ? await acceptProposal({ proposalId: snapshot.id, itemId, userId: userId as string })
      : await rejectProposal({ proposalId: snapshot.id, itemId });
    setProposalBusy(false);
    if (!result.success) {
      setProposalError(result.error ?? 'The suggestion could not be recorded.');
      return;
    }
    setProposalDecided({
      proposal: snapshot,
      text: accept
        ? 'Accepted. History records it as an agent’s suggestion accepted by you.'
        : 'Dismissed. History records that the suggestion was dismissed.',
    });
    void loadItemById(itemId);
  };

  const watchItem = useWatchesStore((s) => s.watchItem);
  const unwatchItem = useWatchesStore((s) => s.unwatchItem);

  const model = buildItemDetailModel(shownItem, data, st, names, {
    actionPlan:
      inAction && activePlan
        ? {
            name: activePlan.name,
            href: activePlan.href,
            planCycleId: activePlan.planCycleId,
            mayAmend: planHold?.mayAmend ?? false,
            // A field the plan holds can still change for a cycle ranking the item again (roadmap S-37).
            rankingCycles: (standing?.cycles ?? [])
              .filter((c) => c.mode === 'ranking')
              .map((c) => ({ name: c.name, href: itemInContextHref(itemId, { cycleId: c.id, tab: rawTab }) })),
          }
        : null,
    currentWords: planHold ? { title: item.title, description: item.description ?? '' } : null,
    heldVersion: planHold?.version ?? null,
    cycleShown: cycleShown ? { id: cycleShown.id, name: cycleShown.name, mode: cycleShown.mode } : null,
    drift,
    onDriftChanged: () => setDriftReload((n) => n + 1),
    save: async (updates) => {
      const result = await updateItem(itemId, updates);
      if (result.ok) return null;
      void loadItemById(itemId);
      return result.reason;
    },
    transferOwner: async (newOwner) => {
      const result = await transferOwnership(itemId, newOwner);
      return result.ok ? null : result.reason;
    },
    openIntegrations: () => setIntegrationsOpen(true),
    openPrint: () => navigate(`/prioritization/items/${itemId}/print`),
    openVisibility: () => setVisibilityOpen(true),
    copyLink: () => {
      void navigator.clipboard
        ?.writeText(`${window.location.origin}/prioritization/items/${itemId}`)
        .then(() => showToast('success', 'Link copied'))
        .catch(() => showToast('error', 'Could not copy the link'));
    },
    retire: item.exit_status !== 'archived' ? () => setRetireOpen(true) : null,
    lineageAction: item.exit_status !== 'archived' && mayEditItem ? setLineageAct : null,
    takeBack: mayTakeBack ? () => setTakeBackOpen(true) : null,
    inspect: isAdmin ? () => void openDebug() : null,
    openDiscussion: () => {
      setTab('overview');
      window.setTimeout(() => document.getElementById('item-discussion')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    },
    toggleWatch: () => void (st.watching ? unwatchItem(itemId) : watchItem(itemId)),
    askAi,
    addEvidence: () => setEvidenceForm({ evidence: null }),
    goToAssessment: () => setTab('assessment'),
    mayEdit: item.exit_status !== 'archived' && mayEditItem,
    editEvidence: (ev) => setEvidenceForm({ evidence: ev }),
    linkObjective:
      item.exit_status !== 'archived' && mayEditItem && userId
        ? async (objectiveId, strength, goalId) => {
            const result = await linkItemToObjective({ itemId, objectiveId, userId, strength, goalId });
            if (!result.ok) return result.message;
            recordItemFieldChange(itemId, 'objectiveLink', null, {
              objective: data.strategy?.objectives.find((o) => o.id === objectiveId)?.name ?? null,
              goal: goalId ? data.strategy?.goals.find((g) => g.id === goalId)?.name ?? null : null,
              strength: strength ?? null,
            });
            data.strategy?.reload();
            return null;
          }
        : null,
    unlinkObjective:
      item.exit_status !== 'archived' && mayEditItem
        ? (mappingIds) => {
            const link = data.strategy?.groups.flatMap((g) => g.objectives).find((l) => l.mappingIds.some((id) => mappingIds.includes(id)));
            void Promise.all(mappingIds.map((id) => deleteMapping(id))).then(() => {
              recordItemFieldChange(itemId, 'objectiveLink', { objective: link?.objective.name ?? null }, null);
              data.strategy?.reload();
            });
          }
        : null,
    unlinkGoal:
      item.exit_status !== 'archived' && mayEditItem
        ? (mappingId, objectiveRowCount) => {
            // The objective's last row keeps the objective link, without the goal.
            const link = data.strategy?.groups.flatMap((g) => g.objectives).find((l) => l.linkedGoals.some((lg) => lg.mappingId === mappingId));
            const goal = link?.linkedGoals.find((lg) => lg.mappingId === mappingId)?.goal;
            void (objectiveRowCount > 1 ? deleteMapping(mappingId) : clearMappingGoal(mappingId)).then(() => {
              recordItemFieldChange(itemId, 'goalLink', { objective: link?.objective.name ?? null, goal: goal?.name ?? null }, null);
              data.strategy?.reload();
            });
          }
        : null,
    openObjectives: () => navigate('/prioritization/strategy/objectives'),
    addRelationship: item.exit_status !== 'archived' && mayEditItem ? setRelationshipKind : null,
    findLinks: nexusConfig?.enabled ? () => setNexusOpen(true) : null,
    openJourney: () => navigate(`/prioritization/items/${itemId}/journey`),
    acceptProposal: mayDecideProposal ? () => void decideProposal(true) : null,
    dismissProposal: mayDecideProposal ? () => void decideProposal(false) : null,
    proposalBusy,
    proposalError,
    proposalDecided,
  });

  // Custom fields show on the tab each one names (Settings → Custom Fields → Shows on).
  const fieldsOn = (t: CustomFieldPlacementTab) => (
    <ItemCustomFields tab={t} metadata={item.metadata as Record<string, unknown> | undefined} readOnly={!mayEditItem} onSave={(updates) => void updateItem(itemId, updates as Partial<Item>)} />
  );

  return (
    <div className="mx-auto max-w-[1260px] pb-24">
      <ItemDetailThirdPassView
        model={model}
        tab={tab}
        onTabChange={setTab}
        className="mt-2"
        versionsPanel={<ItemVersionsPanel itemId={itemId} />}
        railFootnote={<StructureVersionFinePrint table="items" id={itemId} mode="started" />}
        deliveryFirst={inAction}
        extraActions={<AskButton home={{ type: 'item', id: itemId, title: item.title }} {...btnSecondary()} />}
        banner={
          <>
            {standing && (
              <ItemCycleBand
                standing={standing}
                cycleNamed={cycleParam !== null}
                rankText={
                  cycleShown
                    ? cycleStandingChip({
                        ofRecord: data.rankings.find((r) => r.standing === 'official' && r.cycleId === cycleShown.id),
                        list: listsInCycle(data.rankings, cycleShown.id, cycleOfTrack)[0],
                      }).text.toLowerCase()
                    : null
                }
                lockingPlanName={activePlan?.name ?? null}
                facts={plansAsFacts(standing, lockingPlans.map((p) => p.planCycleId))}
                onPick={(id) => {
                  setSearchParams(
                    (prev) => {
                      prev.delete('plan');
                      if (id) prev.set('cycle', id);
                      else prev.delete('cycle');
                      return prev;
                    },
                    { replace: true },
                  );
                }}
              />
            )}
            {activePlan && (
              <PlanActionBand
                plans={lockingPlans}
                planId={activePlan.planCycleId}
                onShowRanking={() => setTab('prioritization')}
                onPlan={(id) => setParam('plan', id)}
              />
            )}
          </>
        }
        topSlots={{
          overview: (
            <div className="mt-3 space-y-2">
              <ClassificationStatusBanner itemId={itemId} hideEmpty={atcUnused} />
            </div>
          ),
          connections: (
            <div className="mt-3 space-y-2">
              <InheritedRelationshipsBanner itemId={itemId} />
              {gateIssues.length > 0 && <RelationshipGatesBanner outcomes={gateIssues} blocksOnly itemId={itemId} />}
            </div>
          ),
          history: (
            <div className="mt-3">
              <button {...btnSecondary()} onClick={() => openTrail(itemId)} title="Decisions, valuations, reorders and governance for this item, assembled">
                Open decision trail
              </button>
            </div>
          ),
        }}
        slots={{
          // Discussion sits on the first screen, under What it is (owner, 2026-10-15).
          overview: (
            <>
              <div id="item-discussion" className="scroll-mt-24">
                <OwnedSectionHeading title="Discussion" />
                <div className="mt-3">
                  <EntityCommentsTab entityType="item" entityId={itemId} workspaceId={item.workspace_id ?? getCurrentWorkspaceId() ?? ''} canComment />
                </div>
              </div>
              {fieldsOn('overview')}
              <div id="item-requests" className="scroll-mt-24">
                <AssignmentsSection home={{ type: 'item', id: itemId }} linkedItems={[{ id: itemId, title: item.title }]} />
              </div>
              <OwnedSectionHeading title="How it was created" owned="a person, an agent, or a person reviewing an agent" />
              <div className="mt-3 space-y-2">
                {model.creation && <p className="text-sm text-[var(--text-pri)]">{model.creation}</p>}
                <ItemCreationProvenanceControl itemId={itemId} />
              </div>
            </>
          ),
          assessment: (
            <>
              {(connectors.datadog || connectors.snowflake) && (
                <>
                  <OwnedSectionHeading title="Signals from connected systems" owned="pull them in as evidence" />
                  <div className="mt-3 space-y-4">
                    {connectors.datadog && <ItemDatadogSignalsPanel itemId={itemId} itemTitle={item.title} />}
                    {connectors.snowflake && <ItemSnowflakeQueryPanel itemId={itemId} itemTitle={item.title} />}
                  </div>
                </>
              )}
              {impactEnabled && (
                <>
                  <OwnedSectionHeading title="Impact" owned="actual impact calibrates the cost of delay" />
                  <div className="mt-3">
                    <ItemImpactTab itemId={itemId} />
                  </div>
                </>
              )}
              {fieldsOn('assessment')}
            </>
          ),
          connections: (
            <>
              <OwnedSectionHeading title="Linked systems" owned="the item in other tools, and what Field Mappings copies" />
              <div className="mt-3">
                <ItemLinkedSystemsSection
                  workspaceId={item.workspace_id ?? getCurrentWorkspaceId()}
                  itemId={itemId}
                  links={st.externalLinks}
                  externalId={item.external_id}
                  onLink={() => setIntegrationsOpen(true)}
                />
              </div>
              {clarity.length > 0 && (
                <>
                  <OwnedSectionHeading title="Clarity context" owned="methodology work that names this item" />
                  <div className="mt-3">
                    <ClaritySection
                      connections={clarity}
                      onRemoveConnection={async (connId) => {
                        await deleteConnection(connId);
                        setClarity((prev) => prev.filter((c) => c.id !== connId));
                      }}
                    />
                  </div>
                </>
              )}
              {fieldsOn('connections')}
            </>
          ),
          prioritization: (
            <>
              <OwnedSectionHeading title="Every ranking it has received" owned="with its standing" />
              <div className="mt-3">
                <ItemRankingsSection itemId={itemId} />
              </div>
              <div className="mt-6">
                <ItemDecisionsSection itemId={itemId} onOpenTrail={() => openTrail(itemId)} />
              </div>
              {fieldsOn('prioritization')}
            </>
          ),
          delivery: (
            <>
              <ItemPatternsSection
                itemId={itemId}
                workspaceId={item.workspace_id ?? null}
                workType={item.work_type}
                launchType={(item as { launch_type?: string | null }).launch_type}
                investmentType={(item as { investment_type?: string | null }).investment_type}
              />
              {deliveryEnabled && (
            <>
              <OwnedSectionHeading title="What was delivered" />
              <ItemDeliveryTab
                itemId={itemId}
                refreshTrigger={deliveryRefresh}
                onMarkDelivered={() => setDeliver({ mode: 'initial', previous: null })}
                onReviseDelivery={(previous) => setDeliver({ mode: 'revision', previous })}
              />
            </>
              )}
              {fieldsOn('delivery')}
            </>
          ),
          history: (
            <>
              {lineageForGraph.length > 0 && (
                <>
                  <OwnedSectionHeading title="Lineage graph" owned="split, merge, fork, supersede, retire" />
                  <div className="mt-3">
                    <LineageGraph itemId={itemId} events={lineageForGraph} />
                  </div>
                </>
              )}
              {/* The timeline and the full change log always show (owner, 2026-10-15). */}
              <OwnedSectionHeading title="Timeline and every change" owned="the full record, newest first" />
              <ItemHistoryLineageView itemId={itemId} className="mt-3" />
            </>
          ),
        }}
      />

      {integrationsOpen && <ItemIntegrationsModal itemId={itemId} itemTitle={item.title} onClose={() => setIntegrationsOpen(false)} />}

      <Modal open={visibilityOpen} onClose={() => setVisibilityOpen(false)} title="Who can see it" size="md">
        <ItemVisibilitySection itemId={itemId} />
      </Modal>

      <Modal open={evidenceForm !== null} onClose={() => setEvidenceForm(null)} title={evidenceForm?.evidence ? 'Edit evidence' : 'Add evidence'} size="md">
        {evidenceForm && (
          <EvidenceForm
            itemId={itemId}
            evidence={evidenceForm.evidence ?? undefined}
            attributeOptions={model.assessment.attributeOptions}
            onSave={() => setEvidenceForm(null)}
            onCancel={() => setEvidenceForm(null)}
          />
        )}
      </Modal>

      {deliver && (
        <DeliverItemModal
          itemId={itemId}
          itemTitle={item.title}
          mode={deliver.mode}
          previousOutcome={deliver.previous}
          onClose={() => setDeliver(null)}
          onDelivered={() => {
            setDeliver(null);
            setDeliveryRefresh((k) => k + 1);
            setTab('delivery');
          }}
        />
      )}

      {nexusOpen && (
        <NexusModal
          itemId={itemId}
          itemTitle={item.title}
          itemDescription={item.description ?? undefined}
          itemCategory={item.itemCategory ?? undefined}
          workspaceId={item.workspace_id ?? ''}
          userId={userId ?? undefined}
          onClose={() => setNexusOpen(false)}
        />
      )}

      <IdeaCraftingModal
        open={descriptorOpen}
        onClose={() => setDescriptorOpen(false)}
        title={item.title ?? ''}
        description={item.description ?? ''}
        onApply={async ({ title, description }) => {
          await updateItem(itemId, { title, description });
        }}
      />

      <Modal open={debug !== null} onClose={() => setDebug(null)} title="Assembled item (admin)" size="lg">
        {debug?.loading && <p className="text-sm text-[var(--text-dim)]">Assembling…</p>}
        {debug?.error && <p className="text-sm text-[var(--coral-text)]">{debug.error}</p>}
        {debug?.data && (
          <>
            <div className="mb-2 flex justify-between">
              <span className={LABEL_MONO}>assemble_item</span>
              <button {...btnSecondary()} onClick={() => void navigator.clipboard?.writeText(JSON.stringify(debug.data, null, 2))}>
                Copy JSON
              </button>
            </div>
            <pre className="max-h-[60vh] overflow-auto bg-[var(--surface)] p-3 font-mono text-[12px] text-[var(--text-sec)]">{JSON.stringify(debug.data, null, 2)}</pre>
          </>
        )}
      </Modal>

      <DecisionTrailPanel open={trailItemId != null} onClose={closeTrail} itemId={trailItemId} />

      {(() => {
        const closeRel = () => setRelationshipKind(null);
        const created = () => {
          setRelationshipKind(null);
          void refreshRelationships();
        };
        return (
          <>
            <CreateCanonicalClusterModal isOpen={relationshipKind === 'cluster'} onClose={closeRel} onCreated={created} sourceItemId={itemId} />
            <CreatePackageModal isOpen={relationshipKind === 'package'} onClose={closeRel} onCreated={created} sourceItemId={itemId} />
            <CreateExclusiveModal isOpen={relationshipKind === 'exclusive'} onClose={closeRel} onCreated={created} sourceItemId={itemId} />
            <AddSequenceLinkModal isOpen={relationshipKind === 'sequence'} onClose={closeRel} onCreated={created} anchorItemId={itemId} />
            <AddCompositionModal isOpen={relationshipKind === 'composition'} onClose={closeRel} onCreated={created} anchorItemId={itemId} />
            <AddContainsModal isOpen={relationshipKind === 'contains'} onClose={closeRel} onCreated={created} sourceItemId={itemId} />
            <AddElaborationModal isOpen={relationshipKind === 'elaboration'} onClose={closeRel} onCreated={created} sourceItemId={itemId} />
            <AddCarriesModal isOpen={relationshipKind === 'carries'} onClose={closeRel} onCreated={created} anchorItemId={itemId} />
            <AddToCollectionModal isOpen={relationshipKind === 'collection'} onClose={closeRel} sourceItemId={itemId} />
          </>
        );
      })()}

      <ProvenanceActionModal
        isOpen={lineageAct !== null}
        onClose={() => setLineageAct(null)}
        onComplete={() => {
          setLineageAct(null);
          void loadItemById(itemId);
        }}
        sourceItemId={itemId}
        initialAction={lineageAct ?? 'split'}
      />

      <RetireItemsDialog
        open={retireOpen}
        onClose={() => setRetireOpen(false)}
        items={[{ id: itemId, title: item.title }]}
        onRetired={() => navigate('/prioritization/items')}
        onArchived={() => void loadItemById(itemId)}
      />

      {mayTakeBack && (
        <ReverseActionButton
          hideButton
          open={takeBackOpen}
          onOpenChange={setTakeBackOpen}
          label="Take back"
          confirmTitle="Take back this retired item"
          explain="The item returns to the lists as active. Your reason is kept with its history."
          run={(reason) => restoreRetiredItem(itemId, reason)}
          onDone={() => void loadItemById(itemId)}
        />
      )}
    </div>
  );
}

export default ItemDetailRealPage;
